// 設備（SPEC DEV-01、#me-devices；VMS UX 評審 2026-10-13 P1-5）：表格與樹狀兩種檢視，涵蓋範圍內的三種來源；樹狀依「我的設備」分組
// （我自己的 / 組織授權給我 / 別人分享給我 → 組織 → site → 家戶；外部分享；分享者）→ 設備 → 通道；搜尋、狀態 / 類型篩選、欄位排序、
// 展開收合；列多時虛擬捲動（只畫看得到的列）。
//   - 預設欄位：狀態｜名稱（下方小字路徑「EddyStudio › lab」，不放設備 ID）｜機種｜韌體｜最後上線｜動作；
//     家戶 / 場域、雲端錄影、AI、位置、離線原因收進「欄位 ▾」（存在這個瀏覽器）。表格不超出畫面（固定版面、可截斷、滑過看全文）
//   - 通道列縮排、寫「NVR631 / CH1」，狀態點用通道自己的；篩選時 parent 本身不符合、只因底下有符合的通道 → 灰色分組標題，不算數
//   - 勾選後批次作業列固定在表格上方
// 名稱點進設備詳細頁（DEV-05）；〔新增 ▾〕在外框（DevicesLayout）。
// 網址參數（總覽卡片與全域搜尋的連結）：?status=offline|online、?type=camera|recorder|sensor、?cert=expiring、?q=。
// 數字：「14 台設備 · 5 個通道」（設備不含通道），篩選後跟著變（devtable.filterTree）。
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDetailsDismiss } from '../../components/useDismiss';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDevices } from '../../lib/useDevices';
import { can, sensorKind, type VmsDevice } from '../../lib/devices';
import { macLabel, modelDisplay } from '../../lib/devices';
import { isRecorderHost } from '../playback/view';
import { certState } from '../overview/health';
import { useScope } from '../../lib/scope';
import { leafOf, nodePath } from '../../lib/myDevices';
import { useLiveEvents } from '../../lib/liveEvents';
import { homeLabel } from './useHomeDetails';
import { LinkDialog } from './LinkDialog';
import { BatchDialog } from './BatchDialog';
import { deviceHref } from './DeviceDetailPage';
import { isKinioNode } from './consoleRelay';
import { ttlSummary } from './ttlTarget';
import { filterTree, flattenRows, OPT_COLS, readOptCols, visibleWindow, writeOptCols, type DevRow, type OptCol, type PresenceFilter, type SortKey, type TypeFilter } from './devtable';
import { MoreMenu } from '../../components/MoreMenu';
import { Time } from '../../components/Time';
import { useDeviceExtras } from './useDeviceExtras';
import { pathLabel, useDevicePath } from './path';

/** 列高（px，虛擬捲動用；CSS .grid.devices tr 同一個值）；超過 VIRTUAL_AT 列才虛擬捲動。 */
const ROW_H = 44;
const VIRTUAL_AT = 300;

const TYPES: TypeFilter[] = ['camera', 'recorder', 'sensor'];

export function DevicesPage() {
  const { t } = useTranslation();
  const colsRef = useRef<HTMLDetailsElement>(null);
  useDetailsDismiss(colsRef);
  const nav = useNavigate();
  const { results, tree, loading } = useDevices();
  const { tree: groups, visibleDevices } = useScope();
  const meByKey = useMemo(() => new Map(visibleDevices.map((m) => [`${m.home_id}/${m.device_ref}`, m])), [visibleDevices]);
  const extras = useDeviceExtras();
  const [sp, setSp] = useSearchParams();
  const setParam = (k: string, v: string) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v);
    else n.delete(k);
    setSp(n, { replace: true });
  };
  const q = sp.get('q') ?? '';
  const [view, setView] = useState<'table' | 'tree'>('table');
  const urlStatus = sp.get('status');
  const pres: PresenceFilter = urlStatus === 'online' || urlStatus === 'offline' || urlStatus === 'unknown' ? urlStatus : 'all';
  const urlType = sp.get('type') as TypeFilter;
  const typeF: TypeFilter = TYPES.includes(urlType) ? urlType : '';
  const certF = sp.get('cert') === 'expiring' ? 'expiring' : '';
  const [cols, setColsState] = useState<Set<OptCol>>(readOptCols);
  const toggleCol = (c: OptCol) =>
    setColsState((s) => {
      const n = new Set(s);
      if (n.has(c)) n.delete(c);
      else n.add(c);
      writeOptCols(n);
      return n;
    });
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'name', dir: 1 });
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [linking, setLinking] = useState<VmsDevice | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [batchOpen, setBatchOpen] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const { sensors } = useLiveEvents();
  const filtered = useMemo(() => {
    const now = Date.now();
    const ok = (d: VmsDevice, parent?: VmsDevice) => {
      if (pres !== 'all' && d.presence !== pres) return false;
      if (typeF === 'camera' && !d.isCamera) return false;
      // 錄影主機與憑證看設備本體；通道跟著它的主機
      if (typeF === 'recorder' && !isRecorderHost(parent ?? d)) return false;
      if (typeF === 'sensor' && !sensorKind(d)) return false;
      if (certF === 'expiring') {
        const c = certState((parent ?? d).certNotAfter, now);
        if (c !== 'expired' && c !== 'soon') return false;
      }
      return true;
    };
    return filterTree(tree, ok, q, pres);
  }, [tree, q, pres, typeF, certF]);
  const filtering = pres !== 'all' || !!typeF || !!certF || !!q.trim();
  /** 名稱下方的小字路徑（「EddyStudio › lab」；不含「我自己的」這層來源） */
  const pathOf = useDevicePath();
  const rows = useMemo(
    () =>
      flattenRows(filtered.shown, {
        sort: sort.key,
        dir: sort.dir,
        collapsed,
        match: filtering ? filtered.match : undefined,
        groupOf:
          view === 'tree'
            ? (d) => {
                const me = meByKey.get(d.key);
                if (!me) return { key: d.homeId, label: d.homeName, order: groups.length };
                const leaf = leafOf(me, groups);
                // 群組名從組織名開始寫，不含「我自己的 / 組織授權給我」這層來源（跟名稱下方的路徑同一套，第五輪 B5-13）
                // 別人分享 / 外部分享的群組還是要寫出「別人分享給我」，不然只剩一個人名
                const label = pathLabel(t, nodePath(groups, leaf.key));
                return { key: leaf.key, label: label || d.homeName, order: leaf.order };
              }
            : undefined,
      }),
    [filtered, filtering, sort, collapsed, view, meByKey, groups, t],
  );
  // 勾選、全選只算真的列出來的設備（灰色分組標題不算）
  const deviceRows = useMemo(() => rows.filter((r) => !r.group && !r.dim), [rows]);
  const virtual = rows.length > VIRTUAL_AT;
  // 可見高度在切成虛擬捲動之後（max-height 生效）才量得準：先前畫的列把容器撐高時，render 當下量到的會太大
  const [viewH, setViewH] = useState(800);
  useLayoutEffect(() => {
    if (virtual && wrapRef.current) setViewH(wrapRef.current.clientHeight || 800);
  }, [virtual, rows.length]);
  const win = virtual ? visibleWindow(rows.length, scrollTop, viewH, ROW_H) : { start: 0, end: rows.length };
  const toggle = (key: string) =>
    setCollapsed((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const sortBy = (key: SortKey) => setSort((s) => ({ key, dir: s.key === key ? (-s.dir as 1 | -1) : 1 }));
  const th = (key: SortKey, label: string, cls?: string) => (
    <th className={cls} aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button className="th-sort" onClick={() => sortBy(key)}>
        {label} {sort.key === key ? (sort.dir === 1 ? '▲' : '▼') : ''}
      </button>
    </th>
  );
  const refOf = (d: VmsDevice) => `${d.homeId}/${d.aid}`;
  const reasonOf = (d: VmsDevice) =>
    d.presence === 'offline' && d.presenceReason ? t(`devices.reason_${d.presenceReason}`, { defaultValue: t('devices.reason_other') }) : '';
  const colCount = 7 + cols.size;
  // DEV-08 批次作業：勾選（跨頁面保留到離開這一頁）
  const pick = (key: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  const row = ({ d, depth, group, parent, dim }: DevRow) => {
    if (group)
      return (
        <tr key={'g:' + group.key} className="group-row" data-group={group.key}>
          <td colSpan={colCount}>
            <button
              className="icon-btn"
              aria-label={collapsed.has('g:' + group.key) ? t('devices.expand') : t('devices.collapse')}
              onClick={() => toggle('g:' + group.key)}
            >
              {collapsed.has('g:' + group.key) ? '▸' : '▾'}
            </button>
            <strong>{group.label}</strong> <span className="muted">{t('devices.count', { count: group.count })}</span>
          </td>
        </tr>
      );
    // 通道寫「lstw-16ch / CH1」，全站同一個「/」（第八輪 B8-09）
    const name = parent ? `${parent.name} / ${d.name}` : d.name;
    const more = [
      {
        key: 'console',
        // Kinio Node〔終端機〕（SPEC #node-ttl-vms-terminal）：帶到設備頁「進階 › 診斷」，不自動連線（第三輪 R3-P0-4）
        hidden: !!depth || !isKinioNode(d.model) || !can(d, 'device.read'),
        label: t('ncon.title'),
        onClick: () => nav(`${deviceHref(d)}?tab=advanced#diag`),
      },
      { key: 'link', hidden: !sensorKind(d), label: t('link.button'), onClick: () => setLinking(d) },
    ];
    const reason = reasonOf(d);
    return (
      <tr key={d.key} className={[depth ? 'child-row' : '', dim ? 'dim-row' : ''].filter(Boolean).join(' ')} data-device={d.aid} data-dim={dim ? '' : undefined}>
        <td className="col-pick">
          {!dim && <input type="checkbox" checked={picked.has(d.key)} onChange={() => pick(d.key)} aria-label={t('batch.pick', { name })} />}
        </td>
        <td className="col-status" title={reason || undefined}>
          <span className={`dot ${d.presence}`} />
          {t(`devices.${d.presence}`)}
        </td>
        <td className="col-name" style={{ paddingLeft: 12 + depth * 24 }}>
          <div className="cell-line">
            {d.children.length > 0 && (
              <button className="icon-btn" aria-label={collapsed.has(d.key) ? t('devices.expand') : t('devices.collapse')} onClick={() => toggle(d.key)}>
                {collapsed.has(d.key) ? '▸' : '▾'}
              </button>
            )}
            {/* Kinio Node：滑過多一句「UART 接著：…」（NODE-06；不另開欄、不加副標） */}
            <Link to={deviceHref(d)} title={[name, !depth && isKinioNode(d.model) ? ttlSummary(t, d, tree) : ''].filter(Boolean).join('\n')} data-ttl-summary={(!depth && isKinioNode(d.model) && ttlSummary(t, d, tree)) || undefined}>
              {name}
            </Link>
            {!depth && d.access && d.access.source !== 'own' && (
              <span className={`chip src-${d.access.source}`} data-source={d.access.source}>
                {t(`scope.src_${d.access.source}`)}
              </span>
            )}
            {d.children.length > 0 && <span className="badge">{t('devices.channels', { n: d.children.length })}</span>}
            {sensorKind(d) && (
              <span className={`badge sensor-state ${sensors.get(d.key)?.active ? 'active' : ''}`} data-sensor-state={d.key}>
                {t(`sensor.${sensorKind(d)}_${sensors.get(d.key)?.active ? 'on' : 'off'}`)}
              </span>
            )}
          </div>
          {!depth && macLabel(d.macSuffix) && (
            <div className="muted small cell-line" data-mac-suffix={d.macSuffix}>
              {macLabel(d.macSuffix)}
            </div>
          )}
          {!depth && view === 'table' && (
            <div className="muted small cell-line" data-path title={pathOf(d)}>
              {pathOf(d)}
            </div>
          )}
        </td>
        {/* 機種欄放不下會截斷：滑過看完整機種名與型號代號（第六輪 B6-13） */}
        <td title={[modelDisplay(t, d), d.model && d.model.toUpperCase() !== modelDisplay(t, d) ? d.model.toUpperCase() : ''].filter(Boolean).join(' · ')} data-label={t('devices.col_model')} className="col-model">
          {modelDisplay(t, d)}
        </td>
        <td title={d.firmware || undefined} data-label={t('devices.col_fw')} className="col-fw">{d.firmware || '—'}</td>
        {cols.has('home') && <td title={d.homeName}>{d.homeName}</td>}
        {cols.has('recording') && <td data-col="recording">{extras.seat.has(refOf(d)) ? t(`devices.seat_${extras.seat.get(refOf(d))}`) : '—'}</td>}
        {cols.has('ai') && <td data-col="ai">{extras.ai.get(refOf(d)) ?? '—'}</td>}
        {cols.has('location') && <td data-col="location">{extras.site.get(d.homeId) ?? '—'}</td>}
        {cols.has('reason') && <td data-col="reason">{reason || '—'}</td>}
        <td className="muted col-seen">{depth ? '' : <Time at={d.lastSeen} rel />}</td>
        <td className="col-actions">
          {d.isCamera && d.presence === 'online' && can(d, 'media.live') && (
            <Link className="btn btn-sm" to="/live" state={{ focus: d.key }}>
              {t('devices.watch')}
            </Link>
          )}
          <MoreMenu label={t('devices.more', { name })} items={more} />
        </td>
      </tr>
    );
  };

  return (
    <section>
      <div className="toolbar wrap">
        <input className="input" placeholder={t('devices.search')} value={q} onChange={(e) => setParam('q', e.target.value)} aria-label={t('devices.search')} />
        <select
          className="input"
          style={{ width: 'auto' }}
          value={pres}
          onChange={(e) => setParam('status', e.target.value === 'all' ? '' : e.target.value)}
          aria-label={t('devices.filter_status')}
        >
          <option value="all">{t('devices.filter_all')}</option>
          <option value="online">{t('devices.online')}</option>
          <option value="offline">{t('devices.offline')}</option>
          <option value="unknown">{t('devices.unknown')}</option>
        </select>
        <select className="input" style={{ width: 'auto' }} value={typeF} onChange={(e) => setParam('type', e.target.value)} aria-label={t('devices.filter_type')}>
          <option value="">{t('devices.type_all')}</option>
          {TYPES.map((x) => (
            <option key={x} value={x}>
              {t(`devices.type_${x}`)}
            </option>
          ))}
        </select>
        {certF && (
          <button className="chip" onClick={() => setParam('cert', '')} data-testid="dev-filter-cert">
            {t('devices.filter_cert')} ✕
          </button>
        )}
        <div className="seg" role="group" aria-label={t('devices.view')}>
          <button aria-pressed={view === 'table'} onClick={() => setView('table')}>
            {t('devices.view_table')}
          </button>
          <button aria-pressed={view === 'tree'} onClick={() => setView('tree')}>
            {t('devices.view_tree')}
          </button>
        </div>
        <details className="col-picker" data-testid="dev-cols" ref={colsRef}>
          <summary className="btn btn-sm">{t('devices.cols')}</summary>
          <div className="col-picker-menu">
            {OPT_COLS.map((c) => (
              <label key={c} className="check">
                <input type="checkbox" checked={cols.has(c)} onChange={() => toggleCol(c)} /> {t(`devices.col_${c}`)}
              </label>
            ))}
          </div>
        </details>
        <span className="muted" data-testid="dev-count" title={t('devices.count_tip')}>
          {loading ? t('common.loading') : t('devices.count2', { count: filtered.devices, channels: filtered.channels })}
        </span>
      </div>
      {results
        .filter((r) => r.error)
        .map((r) => (
          <p key={r.home.home_id} className="error">
            {t('devices.home_failed', { home: homeLabel(r.home), err: String((r.error as Error)?.message ?? r.error) })}
          </p>
        ))}
      {picked.size > 0 && (
        // 批次作業列浮在畫面底部、不推動表格（SPEC DEV-01；第三輪 B3-14）
        <div className="batch-bar dev-batch" data-testid="batch-bar">
          <span>{t('batch.picked', { count: picked.size })}</span>
          <button className="btn btn-primary btn-sm" onClick={() => setBatchOpen(true)} data-testid="batch-open">
            {t('batch.open', { n: picked.size })}
          </button>
          <button className="btn btn-sm" onClick={() => setPicked(new Set())}>
            {t('batch.clear')}
          </button>
        </div>
      )}
      <div
        className={`table-wrap${virtual ? ' virtual' : ''}`}
        ref={wrapRef}
        onScroll={virtual ? (e) => setScrollTop(e.currentTarget.scrollTop) : undefined}
        data-rows={rows.length}
      >
        <table className="grid devices fixed">
          <colgroup>
            <col className="w-pick" />
            <col className="w-status" />
            <col className="w-name" />
            <col className="w-model" />
            <col className="w-fw" />
            {OPT_COLS.filter((c) => cols.has(c)).map((c) => (
              <col key={c} className={`w-${c}`} />
            ))}
            <col className="w-seen" />
            <col className="w-actions" />
          </colgroup>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  aria-label={t('batch.pick_all')}
                  checked={deviceRows.length > 0 && deviceRows.every((r) => picked.has(r.d.key))}
                  onChange={(e) => setPicked(e.target.checked ? new Set(deviceRows.map((r) => r.d.key)) : new Set())}
                />
              </th>
              {th('status', t('devices.col_status'))}
              {th('name', t('devices.col_name'))}
              {th('model', t('devices.col_model'))}
              {th('firmware', t('devices.col_fw'))}
              {cols.has('home') && th('home', t('devices.col_home'))}
              {cols.has('recording') && <th>{t('devices.col_recording')}</th>}
              {cols.has('ai') && <th>{t('devices.col_ai')}</th>}
              {cols.has('location') && <th>{t('devices.col_location')}</th>}
              {cols.has('reason') && <th>{t('devices.col_reason')}</th>}
              {th('last_seen', t('devices.col_last_seen'))}
              <th>
                <span className="sr-only">{t('devices.col_actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {virtual && win.start > 0 && (
              <tr aria-hidden style={{ height: win.start * ROW_H }}>
                <td colSpan={colCount} />
              </tr>
            )}
            {rows.slice(win.start, win.end).map(row)}
            {virtual && win.end < rows.length && (
              <tr aria-hidden style={{ height: (rows.length - win.end) * ROW_H }}>
                <td colSpan={colCount} />
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={colCount} className="muted">
                  {t('devices.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {batchOpen && <BatchDialog devices={deviceRows.filter((r) => picked.has(r.d.key)).map((r) => r.d)} close={() => setBatchOpen(false)} />}
      <LinkDialog sensor={linking} close={() => setLinking(null)} />
    </section>
  );
}
